import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { avanzoApi } from "@/lib/avanzoApi";
import PaesImage from "@/components/PaesImage";
import PostEvalBuddy from "./PostEvalBuddy";

/**
 * /avanzo/simulacro-1 — Simulacro 1 ejecutable (Fase 6).
 *
 * Estados UI: locked | intro | in_progress | completed | error
 *
 * Reglas:
 *  - Nunca mostrar respuesta_correcta / is_correct durante ejecución.
 *  - Nunca hint / feedback / rationale durante ejecución.
 *  - Cronómetro de tiempo transcurrido (no cuenta regresiva).
 *  - Sin hard-stop a 70/75 min.
 *  - Refresh recupera el mismo run/orden.
 *  - Al confirmar respuesta, es inmutable (no back).
 */
const SUBJECT_LABEL = {
  matematica: "Precálculo",
  ciencias:   "Ciencia y Tecnología",
  sociales:   "Ciudadanía y Valores",
  lenguaje:   "Lenguaje y Literatura",
};

// Reverse map to get subject key from label (for color coding).
const LABEL_TO_KEY = Object.fromEntries(
  Object.entries(SUBJECT_LABEL).map(([k, v]) => [v, k])
);

export default function AvanzoSimulation1() {
  const [phase, setPhase] = useState("loading");
  const [gate, setGate]   = useState(null);
  const [result, setResult] = useState(null);
  const [current, setCurrent] = useState(null);
  const [choice, setChoice] = useState(null);
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const startTsRef = useRef(null);
  const submittingRef = useRef(false);

  const refreshStatus = useCallback(async () => {
    setPhase("loading");
    try {
      const r = await avanzoApi.simulation1Status();
      const st = r.data || {};
      setGate(st.gate || null);
      if (st.gate?.already_completed) {
        setResult(st.result || null);
        setPhase("completed");
        return;
      }
      if (st.gate?.in_progress) {
        await enterExecution();
        return;
      }
      if (st.gate?.unlocked) {
        setPhase("intro");
      } else {
        setPhase("locked");
      }
    } catch (e) {
      setError(e); setPhase("error");
    }
  }, []);

  const enterExecution = useCallback(async () => {
    try {
      const cur = await avanzoApi.simulation1Current();
      const data = cur.data || {};
      if (data.finished) {
        const r = await avanzoApi.simulation1Result();
        setResult(r.data);
        setPhase("completed");
        return;
      }
      setCurrent(data);
      // Cronómetro desde started_at server-side (persistente entre refresh)
      const startedIso = data.started_at
        || current?.started_at || new Date().toISOString();
      startTsRef.current = new Date(startedIso).getTime();
      setPhase("in_progress");
    } catch (e) {
      setError(e); setPhase("error");
    }
  }, [current]);

  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  // Cronómetro
  useEffect(() => {
    if (phase !== "in_progress" || !startTsRef.current) return;
    const id = setInterval(() => {
      setElapsed(Math.max(0, Math.floor(
        (Date.now() - startTsRef.current) / 1000
      )));
    }, 1000);
    return () => clearInterval(id);
  }, [phase]);

  const startRun = async () => {
    setPhase("loading");
    try {
      await avanzoApi.simulation1Start();
      await enterExecution();
    } catch (e) {
      setError(e); setPhase("error");
    }
  };

  const confirmAndContinue = async () => {
    if (!choice || submittingRef.current || !current?.item) return;
    submittingRef.current = true;
    try {
      const rt = startTsRef.current
        ? (Date.now() - startTsRef.current) / 1000 : null;
      const r = await avanzoApi.simulation1Answer({
        run_id: current.run_id,
        item_id: current.item.item_id,
        selected_answer: choice,
        response_time_seconds: rt,
      });
      const data = r.data || {};
      if (data.finished) {
        const rr = await avanzoApi.simulation1Result();
        setResult(rr.data);
        setPhase("completed");
        return;
      }
      // siguiente ítem
      setChoice(null);
      await enterExecution();
    } catch (e) {
      setError(e);
      setPhase("error");
    } finally {
      submittingRef.current = false;
    }
  };

  if (phase === "loading") {
    return <div className="avz-empty" data-testid="avz-s1-loading">
      Cargando Simulacro 1…
    </div>;
  }
  if (phase === "error") {
    return (
      <div className="avz-card" data-testid="avz-s1-error">
        <h2 className="avz-h2">No pudimos cargar tu Simulacro 1.</h2>
        <p className="avz-mute avz-mt-4" data-testid="avz-s1-error-detail">
          {error?.response?.data?.detail === "simulation_bank_unavailable"
            ? "El Simulacro 1 no está disponible en este momento. Tu intento no se consumió; vuelve a intentarlo más tarde."
            : "Intenta de nuevo. Si el problema persiste, contacta a soporte."}
        </p>
        <button className="avz-btn avz-btn-primary avz-mt-4"
                onClick={refreshStatus} data-testid="avz-s1-retry">
          Reintentar
        </button>
      </div>
    );
  }
  if (phase === "locked") {
    return <LockedCard gate={gate} />;
  }
  if (phase === "intro") {
    return <IntroCard onStart={startRun} />;
  }
  if (phase === "completed") {
    return <ResultCard result={result} />;
  }

  // in_progress
  const item = current?.item || {};
  const idx = Number(current?.current_index || 0);
  const total = Number(current?.total || 40);
  return (
    <div data-testid="avz-s1-exec">
      <div className="avz-row-between avz-mt-4" style={{ marginBottom: 10 }}>
        <span className="avz-mute" data-testid="avz-s1-position">
          Actividad {idx + 1} de {total}
        </span>
        <span className="avz-chip" data-testid="avz-s1-elapsed">
          {formatElapsed(elapsed)}
        </span>
      </div>
      <section className="avz-card">
        {item.contexto_estimulo && (
          <p className="avz-lead" data-testid="avz-s1-context">
            {item.contexto_estimulo}
          </p>
        )}
        <PaesImage item={item} />
        <h2 className="avz-h2 avz-mt-4" data-testid="avz-s1-question">
          {item.pregunta}
        </h2>
        <div className="avz-stack avz-mt-4" role="radiogroup"
             aria-label="Opciones de respuesta">
          {["A", "B", "C", "D"].map((letter) => (
            <button key={letter} type="button" className="avz-option"
                    aria-pressed={choice === letter}
                    onClick={() => setChoice(letter)}
                    data-testid={`avz-s1-option-${letter}`}>
              <span className="avz-option-letter">{letter}</span>
              <span>{(item.opciones || {})[letter]}</span>
            </button>
          ))}
        </div>
        <div className="avz-row-between avz-mt-8">
          <span
            className="avz-mute"
            data-testid="avz-s1-subject"
            style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <span
              className="avz-subject-dot"
              aria-hidden="true"
              style={{ background: `var(--avz-subject-${item.materia})` }}
            />
            {SUBJECT_LABEL[item.materia] || item.materia}
          </span>
          <button className="avz-btn avz-btn-primary"
                  disabled={!choice || submittingRef.current}
                  onClick={confirmAndContinue}
                  data-testid="avz-s1-confirm">
            Confirmar y continuar
          </button>
        </div>
      </section>
    </div>
  );
}

// ── Componentes ─────────────────────────────────────────────────────

function LockedCard({ gate }) {
  const sessions = Number(gate?.sessions_after_pc || 0);
  const minS = Number(gate?.min_sessions_after_pc || 4);
  const noPC = gate?.reason === "no_progress_check_completed";
  const subj = Number(gate?.subjects_practiced_ever || 0);
  const minSubj = Number(gate?.min_subjects_practiced || 4);
  return (
    <div className="avz-card" data-testid="avz-s1-locked">
      <h1 className="avz-h1" data-testid="avz-s1-locked-title">
        Tu Simulacro 1 se habilitará pronto
      </h1>
      <p className="avz-lead avz-mt-4">
        Continúa con tu preparación. Cuando hayas completado los pasos que
        te faltan, tu Simulacro 1 estará listo.
      </p>
      <ul className="avz-mt-8" data-testid="avz-s1-locked-checklist">
        <li>
          <span data-testid="avz-s1-check-pc">
            {noPC ? "○" : "✅"} Progress Check completado
          </span>
        </li>
        <li>
          <span data-testid="avz-s1-check-sessions">
            {sessions >= minS ? "✅" : "○"} Training posterior:{" "}
            <span className="avz-strong">{Math.min(sessions, minS)} de {minS} sesiones</span>
          </span>
        </li>
        {!noPC && (
          <li>
            <span data-testid="avz-s1-check-subjects">
              {subj >= minSubj ? "✅" : "○"} Materias practicadas:{" "}
              <span className="avz-strong">{Math.min(subj, minSubj)} de {minSubj}</span>
            </span>
          </li>
        )}
      </ul>
      <div className="avz-row avz-mt-8">
        <Link to="/avanzo/entrenar" className="avz-btn avz-btn-primary"
              data-testid="avz-s1-locked-cta-train">
          Continuar practicando
        </Link>
        <Link to="/avanzo/control-progreso" className="avz-btn avz-btn-secondary"
              data-testid="avz-s1-locked-cta-pc">
          Ver Control de Progreso
        </Link>
      </div>
    </div>
  );
}

function IntroCard({ onStart }) {
  return (
    <div className="avz-card" data-testid="avz-s1-intro">
      <div className="avz-mute">Simulacro 1</div>
      <h1 className="avz-h1" data-testid="avz-s1-intro-title">
        Pon a prueba lo que has avanzado
      </h1>
      <p className="avz-lead avz-mt-4">
        Responderás 40 actividades, 10 por cada área. Este simulacro te
        ayudará a conocer cómo estás respondiendo en una experiencia más
        parecida a AVANZO. No recibirás pistas durante el simulacro.
      </p>
      <p className="avz-lead avz-mt-4">
        Al finalizar te mostraremos en qué áreas vas mejor y cuáles conviene
        seguir reforzando.
      </p>
      <ul className="avz-mt-4 avz-mute">
        <li data-testid="avz-s1-intro-items">· 40 actividades</li>
        <li data-testid="avz-s1-intro-subjects">· 4 áreas</li>
        <li data-testid="avz-s1-intro-duration">· Aproximadamente 60–75 minutos</li>
      </ul>
      <p className="avz-mute avz-mt-4">
        Busca un lugar tranquilo y procura completar el simulacro sin
        interrupciones.
      </p>
      <button className="avz-btn avz-btn-primary avz-mt-8"
              onClick={onStart} data-testid="avz-s1-start">
        Comenzar Simulacro 1
      </button>
    </div>
  );
}

function ResultCard({ result }) {
  if (!result) {
    return <div className="avz-empty">Cargando resultado…</div>;
  }
  const band = result.band || {};
  return (
    <div className="avz-card" data-testid="avz-s1-result">
      <div className="avz-mute">Simulacro 1</div>
      <h1 className="avz-h1" data-testid="avz-s1-result-title">
        Resultado de tu Simulacro 1
      </h1>
      <p className="avz-h2 avz-mt-4" data-testid="avz-s1-result-score">
        {result.total_correct} de {result.total_items}
        <span className="avz-mute"> · </span>
        {Math.round(result.percentage)}%
      </p>
      <p className="avz-lead avz-mt-4" data-testid="avz-s1-result-band">
        {band.label}
      </p>
      <p className="avz-mute avz-mt-4">
        Este resultado nos ayuda a decidir qué conviene reforzar antes de
        tu siguiente etapa de preparación.
      </p>

      <div className="avz-mt-8">
        <div className="avz-h3">Resultados por materia</div>
        <ul className="avz-mt-4" style={{ listStyle: "none", padding: 0 }}>
          {Object.entries(result.subject_results || {}).map(([label, s]) => {
            const key = LABEL_TO_KEY[label];
            return (
              <li
                key={label}
                data-testid={`avz-s1-subject-${slug(label)}`}
                data-subject={key}
                style={{
                  borderLeft: key ? `4px solid var(--avz-subject-${key})` : "none",
                  paddingLeft: key ? 12 : 0,
                  margin: "10px 0",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  {key && (
                    <span
                      className="avz-subject-dot"
                      aria-hidden="true"
                      style={{ background: `var(--avz-subject-${key})` }}
                    />
                  )}
                  <span className="avz-strong">{label}</span>
                </span>
                <span className="avz-mute"> · </span>
                {s.correct} de {s.attempts} · {subjectSignalLabel(s.signal)}
              </li>
            );
          })}
        </ul>
      </div>

      {(result.areas_of_strength || []).length > 0 && (
        <div className="avz-mt-8" data-testid="avz-s1-strengths">
          <div className="avz-h3">Fortalezas observadas</div>
          <ul className="avz-mt-4">
            {result.areas_of_strength.map((s) => <li key={s}>✓ {s}</li>)}
          </ul>
        </div>
      )}

      {(result.areas_to_reinforce || []).length > 0 && (
        <div className="avz-mt-8" data-testid="avz-s1-review">
          <div className="avz-h3">Áreas que conviene seguir reforzando</div>
          <ul className="avz-mt-4">
            {result.areas_to_reinforce.map((s) => <li key={s}>· {s}</li>)}
          </ul>
        </div>
      )}

      <PostEvalBuddy source="simulation_1" reflectionType="POST_SIM1" />

      <div className="avz-row avz-mt-8">
        <Link to="/avanzo/entrenar" className="avz-btn avz-btn-primary"
              data-testid="avz-s1-result-continue">
          Continuar mi preparación
        </Link>
        <Link to="/avanzo/progreso" className="avz-btn avz-btn-secondary"
              data-testid="avz-s1-result-progress">
          Ver mi progreso
        </Link>
      </div>
    </div>
  );
}

function subjectSignalLabel(s) {
  if (s === "fortaleza_observada") return "fortaleza observada";
  if (s === "prioridad_refuerzo")  return "prioridad de refuerzo";
  return "en progreso";
}


function formatElapsed(sec) {
  const m = Math.floor(sec / 60);
  const s = String(sec % 60).padStart(2, "0");
  return `${m}:${s}`;
}

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
